import { cn } from '@qr-front/ui/utils'
import { useAtomValue } from 'jotai'
import { AnimatePresence, motion } from 'motion/react'
import { ReactNode, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { isOnServer } from '@/utils'

import { PHONE_PREVIEW_OVERLAY_SLOT_ID } from '../../../components/PhonePreview'
import { openOverlayIdAtom } from '../generatorState'

export const QrScreenOverlay = ({
	id,
	previewMode,
	bottomSlideTransition,
	children,
	className,
}: {
	id: string
	previewMode: boolean
	bottomSlideTransition?: boolean
	children: ReactNode
	className?: string
}) => {
	const openOverlayId = useAtomValue(openOverlayIdAtom)
	const isOpen = openOverlayId === id
	const [container, setContainer] = useState<HTMLElement | null>(null)

	// Handles mount-remount of portal container
	useEffect(() => {
		const updateContainer = () => {
			const el = document.getElementById(PHONE_PREVIEW_OVERLAY_SLOT_ID)
			if (el) {
				setContainer(el)
			}
		}
		updateContainer()
		const observer = new MutationObserver(updateContainer)
		observer.observe(document.body, { childList: true, subtree: true })
		return () => observer.disconnect()
	}, [])

	useEffect(() => {
		if (!previewMode) {
			if (isOpen) {
				document.body.style.overflow = 'hidden'
			} else {
				document.body.style.overflow = ''
			}
		}
	}, [previewMode, isOpen])

	if (isOnServer || !container) {
		return null
	}

	return createPortal(
		<AnimatePresence>
			{isOpen ? (
				<motion.div
					initial={{
						transform: bottomSlideTransition ? 'translateY(100%)' : undefined,
						opacity: 0,
					}}
					animate={{
						transform: bottomSlideTransition ? 'translateY(0)' : undefined,
						opacity: 1,
					}}
					exit={{
						transform: bottomSlideTransition ? 'translateY(100%)' : undefined,
						opacity: 0,
					}}
					transition={bottomSlideTransition ? { duration: 0.5 } : undefined}
					className={cn(
						'relative bottom-0 left-0 right-0 flex h-full items-center justify-center bg-white',
						previewMode ? 'absolute' : 'fixed h-dvh',
						className,
					)}
				>
					<div
						className={cn(
							'absolute bottom-0 left-0 right-0',
							previewMode ? 'top-[38px]' : 'top-2',
						)}
					>
						{children}
					</div>
				</motion.div>
			) : null}
		</AnimatePresence>,
		container,
	)
}
